/**
 * R201: Settings Panel — toggle Ctrl+, (или через MicroMenu).
 * Audio: master / music / sfx mute toggles + volume slider.
 * UI: scale 80-150%.
 * Graphics: bloom on/off, postFX intensity (Phase 2).
 * Persists в localStorage 'aiwari-settings'.
 */

import { useEffect, useState } from 'react';
import { playSfx } from '../audio/SfxEngine.js';
import { useModalStackRegister } from '../state/active-modals-store.js';

interface Settings {
  masterMuted: boolean;
  musicMuted: boolean;
  sfxMuted: boolean;
  masterVolume: number;  // 0..1
  musicVolume: number;
  sfxVolume: number;
  uiScale: number;       // 0.8..1.5
  bloomEnabled: boolean;
  showFps: boolean;
  showZoneToast: boolean;
}

const DEFAULTS: Settings = {
  masterMuted: false,
  musicMuted: false,
  sfxMuted: false,
  masterVolume: 1.0,
  musicVolume: 0.6,
  sfxVolume: 0.8,
  uiScale: 1.0,
  bloomEnabled: true,
  showFps: false,
  showZoneToast: true,
};

const STORAGE_KEY = 'aiwari-settings';

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch { return DEFAULTS; }
}
function save(s: Settings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  applyUiScale(s.uiScale);
  window.dispatchEvent(new CustomEvent('aiwari-settings-changed', { detail: { settings: s } }));
}

function applyUiScale(scale: number): void {
  // R232q5cp: CSS-zoom через transform. Применяется к game-root.
  const clamped = Math.max(0.8, Math.min(1.5, scale));
  document.documentElement.style.setProperty('--aiwari-ui-scale', String(clamped));
}

// Apply at module load time (perfectly before any UI mounts).
try { applyUiScale(load().uiScale); } catch { /* no-op */ }

export function SettingsPanel() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Settings>(() => load());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // Ctrl+, opens settings (WotLK 3.3.5a precedent + не конфликт ни с чем).
      if ((e.key === ',' || e.key === '<' || e.key === 'б' || e.key === 'Б') && e.ctrlKey) {
        e.preventDefault();
        setOpen((o) => !o);
        if (!open) playSfx('click');
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    // R232q5az: still listen to legacy 'aiwari-open-settings' event in
    // case any caller still dispatches it.
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('aiwari-open-settings', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('aiwari-open-settings', onOpenEvent);
    };
  }, [open]);

  // R232q5az M01: register on global modal stack.
  useModalStackRegister('settings', open, () => setOpen(false));

  const update = (patch: Partial<Settings>): void => {
    const next = { ...settings, ...patch };
    setSettings(next);
    save(next);
  };

  const reset = (): void => {
    setSettings(DEFAULTS);
    save(DEFAULTS);
    playSfx('click');
  };

  if (!open) return null;

  return (
    <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="rounded-xl p-5 max-w-[520px] w-[92vw] max-h-[88vh] overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, #1A0F0A, #0E0807)',
          border: '2px solid #06B6D4',
          boxShadow: '0 0 32px rgba(6,182,212,0.4)',
          fontFamily: 'system-ui',
        }}>
        <div className="flex items-baseline justify-between mb-3 pb-2 border-b" style={{ borderColor: '#06B6D440' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8DCC0]Muted">Настройки</div>
            <h2 className="text-xl font-bold" style={{ color: '#22D3EE', fontFamily: 'Cinzel, serif' }}>
              ⚙ Настройки
            </h2>
          </div>
          <button onClick={() => setOpen(false)} className="text-[#E8DCC0]Muted hover:text-[#E8DCC0] text-xl">×</button>
        </div>

        <SectionHead>Звук</SectionHead>
        <Toggle label="Общий звук" value={!settings.masterMuted} onChange={(v) => update({ masterMuted: !v })} />
        <Slider label="Общая громкость" value={settings.masterVolume} onChange={(v) => update({ masterVolume: v })} disabled={settings.masterMuted} />
        <Toggle label="Музыка" value={!settings.musicMuted} onChange={(v) => update({ musicMuted: !v })} />
        <Slider label="Громкость музыки" value={settings.musicVolume} onChange={(v) => update({ musicVolume: v })} disabled={settings.musicMuted || settings.masterMuted} />
        <Toggle label="Эффекты (SFX)" value={!settings.sfxMuted} onChange={(v) => update({ sfxMuted: !v })} />
        <Slider label="Громкость эффектов" value={settings.sfxVolume} onChange={(v) => update({ sfxVolume: v })} disabled={settings.sfxMuted || settings.masterMuted} />

        <SectionHead>Интерфейс</SectionHead>
        <Slider label={`Масштаб интерфейса (${(settings.uiScale * 100).toFixed(0)}%)`} value={settings.uiScale} min={0.8} max={1.5} onChange={(v) => update({ uiScale: v })} />
        <Toggle label="Уведомления о входе в зону" value={settings.showZoneToast} onChange={(v) => update({ showZoneToast: v })} />

        <SectionHead>Графика</SectionHead>
        <Toggle label="Свечение (блум)" value={settings.bloomEnabled} onChange={(v) => update({ bloomEnabled: v })} />
        <Toggle label="Показать счётчик FPS" value={settings.showFps} onChange={(v) => update({ showFps: v })} />

        <SectionHead>Управление</SectionHead>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            window.dispatchEvent(new CustomEvent('aiwari-open-keybinds'));
          }}
          className="w-full py-2 mb-1.5 rounded text-[12px] font-bold uppercase"
          style={{
            background: 'rgba(40,30,20,0.5)',
            border: '1px solid #06B6D420',
            color: '#22D3EE',
          }}
        >⌨ Назначить клавиши…</button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            window.dispatchEvent(new CustomEvent('aiwari-open-macros'));
          }}
          className="w-full py-2 mb-1.5 rounded text-[12px] font-bold uppercase"
          style={{
            background: 'rgba(40,30,20,0.5)',
            border: '1px solid #06B6D420',
            color: '#E8C77B',
          }}
        >📜 Создать макрос…</button>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={reset}
            className="flex-1 py-2 rounded text-[12px] font-bold uppercase tracking-wider"
            style={{ background: 'rgba(40,30,20,0.5)', color: '#9CA3AF', border: '1px solid #4445' }}
          >
            Сбросить по умолчанию
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex-1 py-2 rounded text-[12px] font-bold uppercase tracking-wider"
            style={{
              background: 'linear-gradient(180deg, #22D3EE, #06B6D4)',
              color: '#0E0807',
              border: '1px solid #22D3EE',
            }}
          >
            ✓ Готово
          </button>
        </div>

        <div className="mt-4 text-[10px] text-[#E8DCC0]Muted italic">
          Ctrl+, — открыть/закрыть. Настройки сохраняются автоматически.
        </div>
      </div>
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] uppercase tracking-wider text-[#E8C77B] mt-3 mb-2">{children}</div>;
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="rounded-md px-3 py-2 mb-1.5 flex items-center justify-between"
      style={{ background: 'rgba(40,30,20,0.5)', border: '1px solid #06B6D420' }}>
      <span className="text-[12px] text-[#E8DCC0]">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="px-2.5 py-0.5 rounded text-[10px] font-bold uppercase"
        style={{
          background: value ? '#22D3EE' : 'rgba(50,50,50,0.5)',
          color: value ? '#0E0807' : '#9CA3AF',
          border: `1px solid ${value ? '#22D3EE' : '#4445'}`,
        }}
      >
        {value ? 'On' : 'Off'}
      </button>
    </div>
  );
}

function Slider({ label, value, onChange, min = 0, max = 1, disabled }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; disabled?: boolean }) {
  return (
    <div className="rounded-md px-3 py-2 mb-1.5"
      style={{ background: 'rgba(40,30,20,0.5)', border: '1px solid #06B6D420', opacity: disabled ? 0.5 : 1 }}>
      <div className="text-[11px] text-[#E8DCC0] mb-1">{label}</div>
      <input
        type="range"
        min={min}
        max={max}
        step={0.05}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full"
        style={{ accentColor: '#22D3EE' }}
      />
    </div>
  );
}
